/* eslint-disable react-doctor/no-tiny-text */
"use client"

import { useState, useEffect, useMemo, useRef, useCallback } from "react"
import { TESLA_FACTS } from "@/lib/tesla-facts"
import dynamic from "next/dynamic"
import { useSearchParams } from "next/navigation"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { InstallCheckin } from "@/components/ui/install-checkin"
import { NewSinceLine } from "@/components/ui/new-since-line"
import { usePaginatedSounds, PAGE_SIZE, type SoundFilters } from "@/hooks/usePaginatedSounds"
import { SoundCardSkeletonGrid } from "@/components/ui/sound-card-skeleton"
import {
  Download,
  Keyboard,
} from "lucide-react"
import SearchFilters, { RESULTS_ANCHOR_ID, type SearchFiltersHandle } from "@/components/ui/search-filters"
import { STICKY_BOTTOM_VAR } from "@/hooks/useStickyScrollReserve"
import type { FilterRailState } from "@/components/ui/tls/filter-rail"
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts"
import { useFilterSync, parseSortParam } from "./use-filter-sync"
import SoundGrid from "./sound-grid"
import { markInPlaceNavigation, useSoundsScrollRestoration } from "@/hooks/useSoundsScrollRestoration"

// FilterRail is now ONLY the sub-`lg` filter sheet (the desktop 280px aside
// is gone — see the component's own note: the DS Library template has no
// facet rail, and having both a rail and a pill row meant one piece of state
// behind two controls). The sheet is closed by default and never part of
// first paint, so it stays code-split off the hydration-blocking bundle
// (TES-855). It occupies no layout box now, so there is nothing to reserve
// and no CLS to introduce.
const FilterRail = dynamic(
  () => import("@/components/ui/tls/filter-rail").then((m) => m.FilterRail),
  { ssr: false, loading: () => null }
)
// Keyboard-shortcuts dialog only mounts when the user presses `?` / clicks the
// help affordance — pure post-interaction UI. Splitting its markup + focus-trap
// out of the initial chunk further trims hydration cost.
const KeyboardShortcutsDialog = dynamic(
  () => import("@/components/ui/sounds/keyboard-shortcuts-dialog"),
  { ssr: false, loading: () => null }
)
// SoundsEmptyState only mounts on a zero-results query — it isn't part of the
// first paint, so split it off the hydration chunk to cut /sounds TBT (TES-1024).
const SoundsEmptyState = dynamic(() => import("./sounds-empty-state"), {
  ssr: false,
  loading: () => null,
})
import { useAudioPlayerContext } from "@/contexts/audio-player-context"
import { announce } from "@/lib/announce"
import { type Sound, type Category } from "@/lib/supabase"
import { fetchCategories } from "@/lib/api/client"
import { formatCatalogCount } from "@/lib/format-count"
import { InFeedAd } from "@/components/ui/ad-placement"
import { useFavoriteSounds } from "@/hooks/useFavoriteSounds"
import { LIBRARY_SEARCH_SETTLE_MS, useLibrarySearchTracking } from "@/hooks/useLibrarySearchTracking"
import { writeSoundsUrl } from "./write-sounds-url"
import { INTENT_SHORTCUTS, intentForQuery } from "@/lib/search-intents"
import {
  trackLibraryZeroResultsViewed,
  trackZeroResultsViewed,
  trackLibraryPageViewed,
} from "@/lib/tracking"

const isDev = process.env.NODE_ENV === 'development'

// Cards hydrated on first paint; every later slot stays a reserved-height
// placeholder and hydrates its SoundCard only as it scrolls near the viewport
// (windowed hydration in SoundGrid). 6 covers the desktop first viewport while
// keeping the mobile first-paint hydration task small (TES-1077).
const EAGER_CARD_RENDER = 6

// De-carded. Both of these drew a filled, bordered, rounded panel around
// content in the library rail and its sub-sections — a container inside the
// aside, which already has its own hairline. Rule 1: content is separated by
// hairlines and type scale, never by boxing it. Kept as named constants so the
// call sites still read as "this is a panel", with a rule instead of a box.
const surfaceCardClassName = "border-t border-[color:var(--tls-border-subtle)] pt-4"
const softInsetClassName = "border-t border-[color:var(--tls-border-subtle)] pt-3"

interface SoundsPageClientProps {
  initialSounds?: Sound[]
  initialCategories?: Category[]
  initialCount?: number
  initialCategoryCounts?: Record<string, number>
}

export default function SoundsPageClient({
  initialSounds,
  initialCategories,
  initialCount,
  initialCategoryCounts,
}: SoundsPageClientProps) {
  // eslint-disable-next-line react-doctor/nextjs-no-use-search-params-without-suspense -- this component is rendered inside a Suspense boundary by its parent (app/sounds/(library)/page.tsx)
  const searchParams = useSearchParams()
  const get = useCallback((key: string) => searchParams.get(key), [searchParams])
  const [categories, setCategories] = useState<Category[]>(initialCategories ?? [])

  // Rail filter changes write category/model/picks into the URL the same way
  // SearchFilters.updateURL does — based on the *current* URL (not rebuilt
  // from scratch) so params owned by other writers (q, sort, page) survive.
  // A filter change also clears any stale ?page= (fresh filters => page 1).
  const syncRailToUrl = useCallback((next: FilterRailState) => {
    const params = new URLSearchParams(searchParams.toString())
    if (next.category !== 'All') params.set('category', next.category)
    else params.delete('category')
    if (next.model) params.set('model', next.model)
    else params.delete('model')
    if (next.picksOnly) params.set('picks', '1')
    else params.delete('picks')
    params.delete('page')
    const queryString = params.toString()
    writeSoundsUrl(queryString ? `/sounds?${queryString}` : '/sounds')
  }, [searchParams])

  // Filter state + rail ↔ search-filters bridge (single source of truth)
  const {
    selectedCategory,
    setSelectedCategory,
    railFilters,
    currentFilters,
    handleSearch,
    handleRailChange,
  } = useFilterSync(get, syncRailToUrl)

  const { toggleFavorite } = useFavoriteSounds()
  const player = useAudioPlayerContext()

  // Filter rail UI state (sheet + spec panel) — deliberately not in the hook
  const [filterSheetOpen, setFilterSheetOpen] = useState(false)
  const [specOpen, setSpecOpen] = useState(false)

  // eslint-disable-next-line react-doctor/no-event-handler -- paginationFilters useMemo; no event handlers here
  const paginationFilters: SoundFilters = useMemo(() => ({
    category: currentFilters.category,
    search: currentFilters.query,
    sortBy: currentFilters.sortBy,
    sortOrder: currentFilters.sortOrder,
    collectionId: get('collection') || undefined,
    teslaModel: currentFilters.model || railFilters.model || undefined,
    premiumOnly: railFilters.picksOnly || undefined,
  }), [currentFilters, get, railFilters.model, railFilters.picksOnly])

  // §04.6 — the hook still fetches one 24-sound batch per `currentPage`
  // (offset-based); this layer walks it forward and concatenates batches so
  // the UI presents "Load N more" instead of prev/next. A ?page=N deep link
  // stays linkable: on mount the hook is driven from page 1 up through N so
  // every earlier batch is actually fetched and accumulated, not skipped.
  const [initialTargetPage] = useState(() => {
    const parsed = Number(get('page'))
    return Number.isFinite(parsed) && parsed > 1 ? Math.floor(parsed) : 1
  })
  // targetPage is state (not a ref) so reading it during render — for
  // isPriming below — doesn't trip the "no ref reads during render" rule.
  const [targetPage, setTargetPage] = useState(initialTargetPage)
  const loadedPagesRef = useRef<Set<number>>(new Set())
  const [accumulatedSounds, setAccumulatedSounds] = useState<Sound[]>(initialSounds ?? [])
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null)

  // Mirror the highest loaded page into ?page= (page 1 = no param) so a
  // shared link reproduces the same batches on reload, same pattern
  // SearchFilters.updateURL uses. Skipped while priming a deep link forward
  // (see the accumulation effect below) so catch-up doesn't spam history.
  const targetPageForUrlRef = useRef(initialTargetPage)
  useEffect(() => { targetPageForUrlRef.current = targetPage }, [targetPage])
  const handlePageChange = useCallback((page: number) => {
    if (page < targetPageForUrlRef.current) return
    const params = new URLSearchParams(searchParams.toString())
    if (page > 1) params.set('page', String(page))
    else params.delete('page')
    const queryString = params.toString()
    const nextUrl = queryString ? `/sounds?${queryString}` : '/sounds'
    // Same route, same list, new ?page — a URL change, not a navigation.
    // Tell the scroll-restoration hook so nothing restores against it.
    markInPlaceNavigation(nextUrl)
    writeSoundsUrl(nextUrl)
  }, [searchParams])

  // Hook always starts at page 1 — deep-link catch-up is driven explicitly
  // below so every intermediate batch is fetched, not just the target page.
  const {
    sounds,
    isLoading,
    isValidating,
    isSettled,
    rescue,
    error: paginationError,
    totalCount,
    currentPage,
    hasNextPage,
    nextPage,
    mutate: mutatePaginatedSounds,
  // eslint-disable-next-line react-doctor/no-event-handler -- nextPage is a hook-returned function, not a DOM event handler; naming is from the pagination hook API
  } = usePaginatedSounds(paginationFilters, {
    sounds: initialSounds,
    count: initialCount,
  }, {
    initialPage: 1,
    onPageChange: handlePageChange,
  })

  // Reset pagination bookkeeping whenever the filter set changes. The rows
  // from the previous key stay on screen until the new key's first batch
  // replaces them (the append effect below swaps on currentPage === 1), so a
  // sort or filter change never flashes an empty list.
  // eslint-disable-next-line react-doctor/no-event-handler -- paginationFilters key derivation, not an event handler
  const filterKey = useMemo(
    () => JSON.stringify(paginationFilters, Object.keys(paginationFilters).sort()),
    [paginationFilters]
  )
  const filterKeyRef = useRef(filterKey)
  useEffect(() => {
    if (filterKeyRef.current === filterKey) return
    filterKeyRef.current = filterKey
    loadedPagesRef.current = new Set()
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing derived pagination state to a real filter-identity change, not a render-triggered loop
    setTargetPage(1)
    // eslint-disable-next-line react-hooks/set-state-in-effect -- same filter-change sync as above
    setLoadMoreError(null)
  }, [filterKey])

  // Append each newly-arrived batch once, then keep walking forward to the
  // deep-linked target page (usually 1 — a no-op past the first batch), and
  // surface a load-more batch that failed inline (the sounds already loaded
  // still work — §04.6 ERROR — so this is never a page-level error).
  useEffect(() => {
    // Do not mark a batch loaded until its key's request settles. The hook
    // disables previous-key data so a filter change cannot append results
    // from the filter that was just left.
    if (isLoading || isValidating) return
    if (paginationError) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reacting to an external fetch failure, the documented case for setState-in-effect
      if (currentPage > 1 && accumulatedSounds.length > 0) setLoadMoreError(paginationError.message)
      return
    }
    if (!isSettled) return
    if (loadedPagesRef.current.has(currentPage)) return
    loadedPagesRef.current.add(currentPage)
    // eslint-disable-next-line react-hooks/set-state-in-effect -- appending a newly-arrived fetch batch is the external-system-sync case this rule exists for
    setAccumulatedSounds(prev => {
      if (currentPage === 1) return sounds
      const seen = new Set(prev.map(s => s.id))
      return [...prev, ...sounds.filter(s => !seen.has(s.id))]
    })
    // eslint-disable-next-line react-hooks/set-state-in-effect -- clearing a previous load-more error once a batch succeeds
    setLoadMoreError(null)
    if (currentPage < targetPage && hasNextPage) {
      nextPage()
    }
  }, [sounds, isLoading, isValidating, isSettled, paginationError, accumulatedSounds.length, currentPage, targetPage, hasNextPage, nextPage])

  const isPriming = currentPage < targetPage
  // The previous key's rows stay up until the new key's first batch lands.
  const rowsAreStale = !isSettled && currentPage === 1 && accumulatedSounds.length > 0
  // While those rows are stale, say what is being fetched (§09: a loading
  // state names its object). "24 of 2,218" over rows that no longer match the
  // box above them reads as the answer.
  const pendingLabel = rowsAreStale
    ? currentFilters.query
      ? `Searching \u201c${currentFilters.query}\u201d\u2026`
      : 'Updating the list\u2026'
    : null
  const isLoadingMore = (isLoading || isValidating || isPriming) && accumulatedSounds.length > 0 && !rowsAreStale

  // §12.8 Scroll restoration — page count is already reproduced by the
  // ?page= URL sync above (handlePageChange); this hook restores only the
  // scrollY offset, and only once every batch implied by the URL has
  // actually finished loading (never against a shorter, still-loading page).
  useSoundsScrollRestoration(!isLoading && !isPriming)

  const handleLoadMore = useCallback(() => {
    setLoadMoreError(null)
    // Retry re-requests the batch that just failed rather than advancing
    // past it (the failed page's offset never got marked loaded above).
    if (loadMoreError) {
      mutatePaginatedSounds()
      return
    }
    if (!hasNextPage) return
    setTargetPage(currentPage + 1)
    nextPage()
  }, [loadMoreError, mutatePaginatedSounds, hasNextPage, currentPage, nextPage])

  // Page-level error only covers the FIRST batch failing to load — once any
  // sounds are on screen, a failed load-more surfaces inline instead
  // (§04.6 ERROR: "the connection dropped, the sounds already loaded still work").
  // Also covers a failed fetch for a new filter key while the previous key's
  // rows are still up: those rows must not pass for the new key's results.
  const firstBatchFailed = Boolean(paginationError) && !isSettled && currentPage === 1

  // Ref for search filter focus (keyboard shortcut /)
  const searchFiltersRef = useRef<SearchFiltersHandle>(null)

  // Scroll to top of page when changing pages
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  // Helper function to add debug info
  const addDebugInfo = (message: string) => {
    if (!isDev) return
    const timestamp = new Date().toLocaleTimeString()
    const debugMessage = `[${timestamp}] [SoundsPage] ${message}`
    console.log(debugMessage)
  }

  // Load categories client-side only when not prefetched by the server
  useEffect(() => {
    if (initialCategories && initialCategories.length > 0) return

    const loadCategories = async () => {
      addDebugInfo('Loading categories...')
      try {
        const categoriesData = await fetchCategories()
        addDebugInfo(`Received ${categoriesData.length} categories`)
        setCategories(categoriesData)
      } catch (err) {
        // Lazy-load the Sentry browser SDK off the hydration-critical chunk —
        // it's only needed on the cold-path category fetch failure (TES-1024).
        const { captureException } = await import('@sentry/browser')
        captureException(err, { tags: { context: 'SoundsPage', action: 'loadCategories' } })
      }
    }
    // The try/catch above already reports a category-fetch failure; this
    // guards the one path it can't — the dynamic Sentry import itself
    // failing — so that edge case can't become a second unhandled rejection.
    loadCategories().catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on mount when no server data
  }, [])

  // Both zero-results variants recover the same way: reset search + rail state
  const clearAllFilters = useCallback(() => {
    handleSearch({ query: '', category: 'All', model: '', sortBy: 'downloads', sortOrder: 'desc' })
    handleRailChange({ category: 'All', model: '', picksOnly: false })
  }, [handleSearch, handleRailChange])

  // A query on its own clears by itself: the category, model and sort stay.
  const clearQuery = useCallback(() => {
    handleSearch({ ...currentFilters, query: '' })
  }, [handleSearch, currentFilters])
  const queryIsAlone =
    Boolean(currentFilters.query) &&
    (!currentFilters.category || currentFilters.category === 'All') &&
    !paginationFilters.teslaModel &&
    !paginationFilters.premiumOnly

  // Deduplication prevents React key warnings from SWR caching edge cases —
  // §04.6: this is now the whole accumulated (loaded-so-far) list, not one
  // page, since every loaded batch stays visible at once under load-more.
  const filteredSounds = useMemo(() => {
    if (accumulatedSounds.length === 0) return []

    const seen = new Set<string>()
    return accumulatedSounds.filter(sound => {
      if (seen.has(sound.id)) return false
      seen.add(sound.id)
      return true
    })
  }, [accumulatedSounds])
  // Nothing to show yet and no settled empty answer for the current key: a
  // skeleton, never the empty state. `isSettled` does not lag a filter change
  // the way isLoading/isValidating do.
  const isInitialLoading =
    !firstBatchFailed && filteredSounds.length === 0 && !(isSettled && sounds.length === 0)

  // Keyboard shortcuts: 1-9 play sound, → loads more, space play/pause, / search, s save, j/k nav, ? help.
  // §04.6 has no prev under load-more — nothing already loaded is ever hidden again.
  const { showHelp: showKeyboardHelp, setShowHelp: setShowKeyboardHelp, shortcutFlash } = useKeyboardShortcuts({
    sounds: filteredSounds,
    onNextPage: handleLoadMore,
    onPrevPage: undefined,
    hasNextPage,
    hasPrevPage: false,
    onScrollToTop: scrollToTop,
    onSearchFocus: useCallback(() => {
      searchFiltersRef.current?.focusSearch()
    }, []),
    onSaveCurrent: useCallback(() => {
      const currentSound = player.currentSound
      if (currentSound) {
        toggleFavorite(currentSound.id)
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- player.currentSound.id is the meaningful dep but it's not a primitive; toggleFavorite is stable
    }, [toggleFavorite]),
    onSoundFocus: useCallback((index: number) => {
      const card = document.querySelector(`[data-sound-index="${index}"]`) as HTMLElement | null
      card?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }, []),
  })

  // Track library page view on mount
  useEffect(() => {
    trackLibraryPageViewed({
      entry_source: get('entry') ?? '',
      sort_by: get('sort') ?? '',
      category: get('category') ?? '',
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once on mount
  }, [])

  useLibrarySearchTracking({
    query: currentFilters.query,
    resultCount: totalCount,
    settled: isSettled && !paginationError,
    category: currentFilters.category,
    model: currentFilters.model,
    rescued: rescue != null,
    source: "sounds_page",
  })

  // Zero results are reported once per filter key, and only from a settled
  // response for that key. A sort or filter change clears the hook's rows a
  // render before its loading flags flip; reading the empty list then logged
  // a zero-results view for every sort change.
  const zeroResultsKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (!isSettled || paginationError) return
    if (totalCount !== 0 || sounds.length !== 0) {
      zeroResultsKeyRef.current = null
      return
    }
    if (zeroResultsKeyRef.current === filterKey) return
    // Search-as-you-type passes through empty states ("mar" before "mario").
    // Only a zero that is still there after the pause library_search waits
    // for counts as one.
    const timer = setTimeout(() => {
      zeroResultsKeyRef.current = filterKey
      trackLibraryZeroResultsViewed({
        query: currentFilters.query,
        category: currentFilters.category,
        sortBy: currentFilters.sortBy,
        sortOrder: currentFilters.sortOrder,
        resultCount: 0,
      })
      trackZeroResultsViewed({
        query: currentFilters.query,
        category: currentFilters.category,
        result_count: 0,
      })
    }, LIBRARY_SEARCH_SETTLE_MS)
    return () => clearTimeout(timer)
  }, [isSettled, paginationError, totalCount, sounds.length, filterKey, currentFilters])

  const intent = useMemo(() => intentForQuery(currentFilters.query), [currentFilters.query])

  // An active ?q= or filter brings the results list under the sticky bar, so
  // the first screen is rows rather than the page header. Skipped on
  // back/forward, where scroll restoration owns the position.
  const hasActiveFilter = Boolean(
    currentFilters.query ||
      (currentFilters.category && currentFilters.category !== 'All') ||
      paginationFilters.teslaModel ||
      paginationFilters.premiumOnly
  )
  const scrolledKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (!hasActiveFilter) {
      scrolledKeyRef.current = null
      return
    }
    if (scrolledKeyRef.current === filterKey) return
    scrolledKeyRef.current = filterKey
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
    if (nav?.type === 'back_forward') return
    const anchor = document.getElementById(RESULTS_ANCHOR_ID)
    if (!anchor) return
    const reserve = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(STICKY_BOTTOM_VAR)) || 0
    if (anchor.getBoundingClientRect().top <= reserve + 16) return
    anchor.scrollIntoView({ block: 'start' })
  }, [filterKey, hasActiveFilter])

  // Fix 3 (WCAG 4.1.3 Status Messages) — search results were never
  // announced: typing "mario" silently drops the list from 1,390 to 7 with
  // no status message on either live region. Root cause is a layer down
  // from SoundGrid's own load-more announcement (app/sounds/sound-grid.tsx):
  // a query change resets accumulatedSounds to [], which swaps the render
  // branch below from <SoundGrid> to <SoundsEmptyState> (or straight back to
  // a non-empty <SoundGrid>) — SoundGrid unmounts/remounts across that gap,
  // so its "did loadedCount increase" ref resets its own baseline to the new
  // count and never sees the transition. This effect lives one level up,
  // keyed on the settled query itself, so it survives that remount.
  //
  // §11.1's single-writer rule: routes through announce() (lib/announce.ts),
  // no new aria-live node — the old broadcast-storm design (aria-live on the
  // 1,400-row container) is exactly what must not come back.
  //
  // "Settled" = SearchFilters' own 300ms debounce already happened before
  // onSearch (handleSearch) ever updates currentFilters.query, and the
  // dedupe ref below only re-announces when the query text itself changes —
  // so this fires once per distinct search, never per keystroke.
  const announcedSearchQueryRef = useRef<string | null>(null)
  useEffect(() => {
    if (isLoading || isValidating || !isSettled) return
    if (announcedSearchQueryRef.current === currentFilters.query) return
    announcedSearchQueryRef.current = currentFilters.query
    if (!currentFilters.query) return
    const found = `${formatCatalogCount(totalCount)} sound${totalCount === 1 ? "" : "s"}`
    announce(
      rescue
        ? `No sounds found for "${currentFilters.query}". ${found} found for "${rescue.term}"`
        : totalCount > 0
          ? `${found} found for "${currentFilters.query}"`
          : `No sounds found for "${currentFilters.query}"`,
      "polite"
    )
  }, [currentFilters.query, isLoading, isValidating, isSettled, totalCount, rescue])

  return (
    <>
      <div className="relative z-10 tls-container pb-12">

        {/* Return-visit install check-in (install-success loop) — renders only
            when a 2h-14d-old pending download exists in localStorage. */}
        <InstallCheckin className="mb-4" />
        <NewSinceLine className="mb-4" />

        {/* Single column. The library's one facet control is the FilterRow
            inside SearchFilters below (pill row at lg+, sheet trigger under
            it). There is no rail column to lay out around.
            translate="no": this subtree re-renders on every filter/page/search
            change via client-side navigation. Browser translators (Chrome,
            Edge) re-parent text nodes into <font> wrappers on each pass, and
            React doesn't know about the move, so the following commit's DOM
            ops throw. TES-1982/PR#993 made removeChild/insertBefore no-op
            instead of throwing for a moved node, but that just leaves stale
            nodes behind instead of removing them, so across several
            client-side navigations in one translated session those
            accumulate. TES-1986 (RangeError: Maximum call stack size
            exceeded) is that accumulation blowing a recursive DOM or
            reconciliation walk, worst on iOS JavaScriptCore's small native
            stack. Every TES-1986 event correlates with an active translator
            (Turkish/Korean translated aria-labels and <font>-wrapped click
            targets in the Sentry breadcrumbs). Excluding this region from
            translation removes the mutation at its source instead of
            patching another downstream recursive function; the static
            hero/header above stays translatable. */}
        <div translate="no">

          {/* The one filter sheet — opened by SearchFilters' `Filters` trigger */}
          {/* eslint-disable-next-line react-doctor/no-event-handler */}
          <FilterRail
            categories={categories}
            categoryCounts={initialCategoryCounts ?? {}}
            totalCount={totalCount}
            filters={railFilters}
            onChange={handleRailChange}
            sheetOpen={filterSheetOpen}
            onSheetClose={() => setFilterSheetOpen(false)}
            specOpen={specOpen}
            onSpecToggle={() => setSpecOpen(o => !o)}
          />

          {/* Main content */}
          <div className="min-w-0">

            {/* Consolidated Search + Filter + Sort Bar (sticky) */}
            <SearchFilters
              ref={searchFiltersRef}
              onSearch={handleSearch}
              initialFilters={{
                category: get('category') || 'All',
                model: get('model') || '',
                query: get('q') || get('search') || '',
                ...parseSortParam(get('sort')),
              }}
              categories={categories}
              categoryCounts={initialCategoryCounts}
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
              model={currentFilters.model}
              picksOnly={railFilters.picksOnly}
              onPicksOnlyChange={(picksOnly) => handleRailChange({ picksOnly })}
              onOpenCategorySheet={() => setFilterSheetOpen(true)}
              onClearFilters={clearAllFilters}
              resultsSummary={
                <>
                  {/* Heading-order bridge: card titles below are h3s, and this
                      results region renders before the page's first visible h2. */}
                  <h2 className="sr-only">Sound results</h2>
                  {/* DS Library template list-header line: "Showing 12 of
                      1,400", the loaded-so-far position. */}
                  <span className="hidden truncate text-sm tabular-nums text-[var(--tls-fg-muted)] sm:inline">
                    {pendingLabel ?? (firstBatchFailed ? '' : totalCount > 0
                      ? `Showing ${formatCatalogCount(filteredSounds.length)} of ${formatCatalogCount(totalCount)} sound${totalCount === 1 ? '' : 's'}`
                      : `${formatCatalogCount(filteredSounds.length)} sound${filteredSounds.length !== 1 ? 's' : ''}`
                    )}
                  </span>
                  {/* Narrow viewports drop the verb, never the numbers: a
                      truncated "Showing 24 of 1,442 sou..." loses the unit. */}
                  <span className="truncate text-sm tabular-nums text-[var(--tls-fg-muted)] sm:hidden">
                    {pendingLabel ?? (firstBatchFailed ? '' : totalCount > 0
                      ? `${formatCatalogCount(filteredSounds.length)} of ${formatCatalogCount(totalCount)}`
                      : `${formatCatalogCount(filteredSounds.length)}`
                    )}
                  </span>
                  <nav aria-label="Browse by feel" className="flex items-center gap-3 text-sm sm:gap-4">
                    <span className="text-[var(--tls-fg-subtle)]">By feel</span>
                    {INTENT_SHORTCUTS.map(shortcut => (
                      <Link
                        key={shortcut.label}
                        href={shortcut.href}
                        prefetch={false}
                        className="tlsr-focus tls-target inline-flex items-center font-semibold text-[var(--tls-fg-muted)] underline decoration-[var(--tls-border-strong)] underline-offset-4 hover:text-[var(--tls-brand-ink)]"
                      >
                        {shortcut.label}
                      </Link>
                    ))}
                  </nav>
                  <button type="button"
                    onClick={() => setShowKeyboardHelp(prev => !prev)}
                    className="relative hidden size-8 shrink-0 items-center justify-center rounded-[var(--ls-radius-round)] text-[var(--tls-fg-subtle)] transition-colors duration-[var(--motion-duration-standard)] hover:text-[var(--tls-fg-strong)] tlsr-focus md:flex"
                    aria-label="Keyboard shortcuts help"
                    title="Press ? for keyboard shortcuts"
                  >
                    <Keyboard className="size-4" aria-hidden="true" />
                    {shortcutFlash && (
                      <span
                        className="absolute -top-1 -right-1 flex items-center justify-center rounded-full bg-[var(--tls-brand)] text-[var(--tls-brand-fg)]"
                        style={{ width: 14, height: 14, fontSize: 9, fontFamily: "monospace" }}
                      >
                        {shortcutFlash === 'space' ? '⎵' : shortcutFlash}
                      </span>
                    )}
                  </button>
                </>
              }
            />

            {!isInitialLoading && rescue && (
              <p className="mt-4 mb-4 text-sm text-[var(--tls-fg-muted)] md:mb-0" data-testid="search-rescue-note">
                {rescue.kind === 'corrected'
                  ? <>No sounds for &ldquo;{rescue.original}&rdquo;. Showing results for </>
                  : <>Nothing matched all of &ldquo;{rescue.original}&rdquo;. Showing results for </>}
                <strong className="font-semibold text-[var(--tls-fg-strong)]">&ldquo;{rescue.term}&rdquo;</strong>.
              </p>
            )}
            {!isInitialLoading && intent && (
              <p className="mt-4 text-sm text-[var(--tls-fg-muted)]" data-testid="search-intent-link">
                Curated page:{' '}
                <Link
                  href={intent.href}
                  className="font-semibold text-[var(--tls-fg-strong)] underline decoration-[var(--tls-border-strong)] underline-offset-4 hover:text-[var(--tls-brand-ink)]"
                >
                  {intent.label} &rarr;
                </Link>
              </p>
            )}

            {/* Content */}
            {/* Keep the skeleton visible while the current key is still
                fetching and the accumulated list has been cleared. */}
            {isInitialLoading ? (
              <SoundCardSkeletonGrid count={12} />
            ) : firstBatchFailed ? (
              <div className="py-24 text-center" role="alert">
                <p className="mb-4 text-lg font-bold text-[var(--tls-fg-strong)]">
                  No sounds loaded. Press Try again, or browse by category.
                </p>
                <Button onClick={() => mutatePaginatedSounds()}>Try again</Button>
              </div>
            ) : filteredSounds.length === 0 ? (
              <SoundsEmptyState
                filters={currentFilters}
                onClearSearch={queryIsAlone ? clearQuery : clearAllFilters}
                onClearFilters={clearAllFilters}
                popular={initialSounds}
              />
            ) : (
              <div aria-busy={rowsAreStale || undefined}>
                <SoundGrid
                  sounds={filteredSounds}
                  eagerCount={EAGER_CARD_RENDER}
                  totalCount={totalCount}
                  hasMore={hasNextPage}
                  isLoadingMore={isLoadingMore}
                  loadMoreError={loadMoreError}
                  onLoadMore={handleLoadMore}
                  batchSize={PAGE_SIZE}
                />
              </div>
            )}

            {/* Ad placement between sound grid and related content */}
            {!isInitialLoading && totalCount > 0 && (
              <InFeedAd slot="8879381652" lazy className="mt-12" />
            )}

            {/* Installation Guide CTA */}
            {!isInitialLoading && (
              <div className={`${surfaceCardClassName} mt-16 flex flex-col items-start justify-between gap-6 p-6 sm:flex-row sm:items-center`}>
                <div className="flex items-start gap-4">
                  <div className={`${softInsetClassName} flex h-12 w-12 shrink-0 items-center justify-center text-[var(--tls-brand)]`}>
                    <Download className="size-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[var(--tls-fg-strong)]">New to custom lock sounds?</h3>
                    <p className="mt-1 text-sm leading-6 text-[var(--tls-fg-muted)]">Change your Tesla lock sound in {TESLA_FACTS.install.label} with our step-by-step guide.</p>
                  </div>
                </div>
                <Button asChild className="min-h-[48px] px-6 text-sm font-semibold">
                  <Link href="/guide">
                    Install guide
                  </Link>
                </Button>
              </div>
            )}

          </div>{/* /main content */}
        </div>{/* /two-column layout */}
      </div>

      {/* Keyboard Shortcuts Help Overlay — code-split, mounts on demand only */}
      {showKeyboardHelp && (
        <KeyboardShortcutsDialog onClose={() => setShowKeyboardHelp(false)} />
      )}
    </>
  )
}
